import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { updateAPI, affiliateBrandsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, XCircle, FileSpreadsheet, Globe, AlertTriangle, Clock, ChevronDown, ChevronUp, RotateCcw, Database, Users, FileCheck, ArrowDownToLine, RefreshCw, Zap } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const STAGE_ICONS = { uploading: ArrowDownToLine, reading: FileSpreadsheet, validating: FileCheck, mapping: Users, processing: Zap, writing: Database, refreshing: RefreshCw, completed: CheckCircle2, error: XCircle };

function ImportProgress({ job, onRetry }) {
  const [showDetails, setShowDetails] = useState(false);
  if (!job) return null;
  const StageIcon = STAGE_ICONS[job.stage] || Loader2;
  const isRunning = !['success', 'error'].includes(job.status);
  const isSuccess = job.status === 'success';
  const isError = job.status === 'error';

  return (
    <div className="space-y-3" data-testid="import-progress">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5"><StageIcon className={`h-3.5 w-3.5 ${isRunning ? 'animate-pulse text-amber-500' : isSuccess ? 'text-emerald-500' : 'text-red-500'}`} /><span className="font-medium">{job.stage_label}</span></div>
          <span className="text-muted-foreground font-mono">{job.progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full transition-all duration-500 ${isError ? 'bg-red-500' : isSuccess ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${job.progress}%` }} /></div>
        {job.detail && <p className="text-[11px] text-muted-foreground">{job.detail}</p>}
      </div>

      {isRunning && job.total_rows > 0 && (
        <div className="flex justify-between text-[11px] text-muted-foreground"><span>{job.processed_rows?.toLocaleString()} / {job.total_rows?.toLocaleString()} rows</span><span className="flex items-center gap-1"><Clock className="h-3 w-3" />{job.elapsed}s</span></div>
      )}

      {isSuccess && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="p-2 rounded bg-muted/50"><span className="text-muted-foreground">Duration</span><br /><strong>{job.duration}s</strong></div>
            <div className="p-2 rounded bg-emerald-500/10"><span className="text-muted-foreground">Matched</span><br /><strong className="text-emerald-500">{job.matched_count}</strong></div>
            <div className="p-2 rounded bg-muted/50"><span className="text-muted-foreground">Total Rows</span><br /><strong>{job.total_rows?.toLocaleString()}</strong></div>
            <div className="p-2 rounded bg-blue-500/10"><span className="text-muted-foreground">Inserted</span><br /><strong className="text-blue-500">{job.inserted}</strong></div>
            <div className="p-2 rounded bg-amber-500/10"><span className="text-muted-foreground">Updated</span><br /><strong className="text-amber-500">{job.updated}</strong></div>
            <div className="p-2 rounded bg-muted/50"><span className="text-muted-foreground">Skipped</span><br /><strong>{job.skipped}</strong></div>
          </div>
          {job.warnings?.length > 0 && <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">{job.warnings.join('; ')}</div>}
          {job.matched?.length > 0 && (
            <div>
              <button onClick={() => setShowDetails(!showDetails)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">{showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{showDetails ? 'Hide' : 'Show'} details ({job.matched.length})</button>
              {showDetails && <div className="mt-2 max-h-48 overflow-y-auto space-y-1">{job.matched.map((m, i) => (
                <div key={i} className="p-2 rounded bg-emerald-500/5 border border-emerald-500/10 text-[11px] grid grid-cols-4 gap-1">
                  <span className="font-medium col-span-4">ID: {m.entity_id}</span>
                  <span>Clicks: {m.clicks}</span><span>Regs: {m.registrations}</span><span>FTD: {m.ftd_count}</span>
                  <span>Dep: {m.deposit_amount?.toLocaleString()}</span>
                </div>
              ))}</div>}
            </div>
          )}
        </div>
      )}

      {isError && (
        <div className="space-y-2">
          <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-400">{job.error || job.detail}</div>
          {onRetry && <Button variant="outline" size="sm" className="w-full" onClick={onRetry}><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Retry</Button>}
        </div>
      )}
    </div>
  );
}

export default function Update() {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [importStatus, setImportStatus] = useState({});
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [brandsRes, statusRes] = await Promise.all([affiliateBrandsAPI.getAll(), updateAPI.getImportStatus()]);
        setBrands(brandsRes.data);
        setImportStatus(statusRes.data);
      } catch {}
    };
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Group brands by name for the selector
  const brandNames = [...new Set(brands.map(b => b.name))].sort();

  const readFileAsBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => { const bytes = new Uint8Array(e.target.result); let bin = ''; bytes.forEach(b => bin += String.fromCharCode(b)); resolve(btoa(bin)); };
    reader.onerror = reject;
    reader.readAsArrayBuffer(f);
  });

  const handleUpload = async () => {
    if (!selectedBrand || !file) { toast.error('Select a brand and file'); return; }

    // Find a brand matching the selected name (any company - first match for config)
    const brand = brands.find(b => b.name === selectedBrand);
    if (!brand) { toast.error('Brand not found'); return; }

    setUploading(true); setJob(null);
    try {
      const content = await readFileAsBase64(file);
      const res = await updateAPI.startImportJob(brand.id, content, file.name);
      setJob({ status: 'running', stage: 'uploading', stage_label: 'Uploading file', progress: 0, detail: 'Starting...' });

      pollRef.current = setInterval(async () => {
        try {
          const p = await updateAPI.getImportJob(res.data.job_id);
          setJob(p.data);
          if (['success', 'error'].includes(p.data.status)) {
            clearInterval(pollRef.current);
            setUploading(false);
            try { const s = await updateAPI.getImportStatus(); setImportStatus(s.data); } catch {}
          }
        } catch { clearInterval(pollRef.current); setUploading(false); }
      }, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      setUploading(false);
    } finally {
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const selectedBrandStatus = importStatus[selectedBrand];
  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <Layout title="Update">
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="update-page">
        <div><h2 className="font-heading text-2xl font-bold">Data Update</h2><p className="text-muted-foreground">Upload standardized 8-metric reports per brand</p></div>

        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Strict Rule-Based Import</p>
              <p className="text-xs text-muted-foreground mt-1">Select a brand → upload its CSV/XLS report with 8 standard columns: Entity ID, Clicks, Registrations, FTD Count, Deposit Amount, Withdrawal Amount, Net Result, Commission. The system uses ONLY brand configuration to parse.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Upload Report</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Brand Selector */}
            <div className="space-y-2">
              <Label>Select Brand *</Label>
              <Select value={selectedBrand} onValueChange={v => { setSelectedBrand(v); setJob(null); }}>
                <SelectTrigger data-testid="brand-select"><SelectValue placeholder="Choose brand..." /></SelectTrigger>
                <SelectContent>
                  {brandNames.map(name => {
                    const b = brands.find(br => br.name === name);
                    return <SelectItem key={name} value={name}><div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{name}<Badge variant="outline" className="text-[9px] ml-1">{b?.provider?.toUpperCase()}</Badge></div></SelectItem>;
                  })}
                </SelectContent>
              </Select>
              {selectedBrandStatus && (
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Clock className="h-3 w-3" /><span>Last: {formatDate(selectedBrandStatus.imported_at)}</span>
                  <span className="mx-1">|</span>
                  <span className={selectedBrandStatus.status === 'success' ? 'text-emerald-500' : 'text-red-500'}>
                    {selectedBrandStatus.status === 'success' ? `${selectedBrandStatus.matched_count} matched` : 'Error'}
                  </span>
                </div>
              )}
            </div>

            {/* File Upload */}
            {selectedBrand && !uploading && !job?.status?.match(/success|error/) && (
              <>
                <div className="p-4 rounded-lg border border-dashed border-border bg-muted/30 text-center">
                  {file ? (
                    <div className="flex items-center justify-center gap-2"><FileSpreadsheet className="h-4 w-4 text-primary" /><span className="text-sm font-medium">{file.name}</span><Badge variant="secondary" className="text-[10px]">{(file.size / 1024).toFixed(0)} KB</Badge></div>
                  ) : (
                    <div><Upload className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">CSV, XLS or XLSX file</p></div>
                  )}
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => fileRef.current?.click()}>{file ? 'Change File' : 'Select File'}</Button>
                </div>
                <Button className="w-full" onClick={handleUpload} disabled={!file}><Upload className="h-4 w-4 mr-2" />Upload & Import</Button>
              </>
            )}

            {/* Progress */}
            {(uploading || job) && (
              <ImportProgress job={job} onRetry={() => { setJob(null); setUploading(false); }} />
            )}
          </CardContent>
        </Card>

        {/* Required Format */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Required CSV Format</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b">{['Entity ID', 'Clicks', 'Registrations', 'FTD Count', 'Deposit Amount', 'Withdrawal Amount', 'Net Result', 'Commission'].map(h => <th key={h} className="p-1.5 text-left font-medium text-muted-foreground">{h}</th>)}</tr></thead>
                <tbody><tr className="text-muted-foreground"><td className="p-1.5 font-mono">8308</td><td className="p-1.5">2264</td><td className="p-1.5">109</td><td className="p-1.5">7</td><td className="p-1.5">1150002.92</td><td className="p-1.5">1281084</td><td className="p-1.5">-131081.08</td><td className="p-1.5">-32770.27</td></tr></tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
